import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { getPlacingForImpactTracker } from '@src/telemetry/utils/impact.utils'
import { getStorage } from '@src/utils/enhancedStorage'

import mixpanelCl from '../../../../../telemetry/integrations/mixpanel'

const ATTENTIVE_ORIGIN = 'https://creatives.attn.tv'

const ATTENTIVE_ACTIONS = {
  EMAIL_LEAD: 'EMAIL_LEAD',
  // @i18n-ignore
  MOBILE_LEAD: 'LEAD',
} as const

// Storage key for attentive captured data
const ATTENTIVE_DATA_STORAGE_KEY = 'attentive_pii'

// Store captured attentive data for later use in CompleteRegistration
const storeAttentiveData = (userData: { email?: string; phone?: string; gender?: string }) => {
  try {
    const storage = getStorage()
    const existingData = storage.getItem(ATTENTIVE_DATA_STORAGE_KEY, { isGlobal: true })
    const mergedData = {
      ...(typeof existingData === 'string' ? JSON.parse(existingData) : existingData || {}),
      ...userData,
    }
    storage.setItem(ATTENTIVE_DATA_STORAGE_KEY, JSON.stringify(mergedData), { isGlobal: true })
  } catch (error) {
    console.error('Error storing attentive data:', error)
  }
}

// Get stored attentive data
const getStoredAttentiveData = (): { email?: string; phone?: string; gender?: string } => {
  try {
    const storage = getStorage()
    const stored = storage.getItem(ATTENTIVE_DATA_STORAGE_KEY, { isGlobal: true })
    if (stored) {
      return typeof stored === 'string' ? JSON.parse(stored) : stored
    }
  } catch (error) {
    console.error('Error retrieving attentive data:', error)
  }
  return {}
}

// Clear stored attentive PII data (call after CompleteRegistration/Purchase for security)
const clearStoredAttentiveData = (): void => {
  try {
    const storage = getStorage()
    storage.removeItem(ATTENTIVE_DATA_STORAGE_KEY, { isGlobal: true })
  } catch (error) {
    console.error('Error clearing attentive data:', error)
  }
}

const handleAttentiveFrameMessages = (event: MessageEvent) => {
  // Security check: Validate origin to prevent spoofing
  if (event.origin !== ATTENTIVE_ORIGIN) {
    return
  }

  const { __attentive } = event.data || {}
  const {
    action,
    email,
    phone: mobileNumber,
    metadata: { Gender: gender } = {
      Gender: undefined,
    },
  } = __attentive || {}

  switch (action) {
    case ATTENTIVE_ACTIONS.EMAIL_LEAD: {
      // Build user data object from attentive data for PII capture
      const user: { email?: string; phone?: string; gender?: string } = {}

      if (email) {
        user.email = email
      }

      if (mobileNumber) {
        // Normalize phone number: keep + for E.164 format, remove spaces/parentheses/dashes
        // This allows +1555... but removes (555) or - or spaces
        let cleaned: string = String(mobileNumber).replace(/[^0-9+]/g, '')
        // Ensure + is only at the start if present
        if (cleaned.includes('+') && !cleaned.startsWith('+')) {
          const digitsOnly = cleaned.replace(/\+/g, '')
          cleaned = `+${digitsOnly}`
        }
        user.phone = cleaned
      }

      if (gender) {
        // Convert gender to lowercase first letter
        user.gender = gender[0]?.toLowerCase() || ''
      }

      // Store the captured data for later use in CompleteRegistration and Lead events
      // All data() and tag() calls are handled in the Blotout integration file (edge-tag/index.ts)
      if (user.email ?? user.phone ?? user.gender) {
        storeAttentiveData(user)
      }

      trackEvent(TELEMETRY_EVENT_NAMES.EMAIL_NEWSLETTER_SUBSCRIBE, {
        email,
        gender,
        // eslint-disable-next-line i18next/no-literal-string
        placing: getPlacingForImpactTracker('popup'),
      })
      mixpanelCl.identify({ email: email, isTentativeEmail: true })
      break
    }
    case ATTENTIVE_ACTIONS.MOBILE_LEAD:
      trackEvent(TELEMETRY_EVENT_NAMES.SMS_NEWSLETTER_SUBSCRIBE, {
        mobileNumber,
        // @i18n-ignore
        // eslint-disable-next-line i18next/no-literal-string
        placing: 'popup',
      })
      break
  }
}

export { ATTENTIVE_ORIGIN, handleAttentiveFrameMessages, getStoredAttentiveData, clearStoredAttentiveData }
